import { get as _get, toNumber as _toNumber } from 'lodash';
import { EntityId, ShopeeStoreId } from '../../config/store-config';
import {
    FinanceFees,
    FinanceOrderRecord,
    emptyFees,
    financeRecordId,
    round2,
    sumFees,
} from './finance-record';

// Shopee → FinanceOrderRecord. Pure: no Angular, no injection, no clock.
// `syncedAt` and `fetchedAt` are passed in so the output is deterministic and
// testable against a stored fixture.
//
// Shopee needs three calls joined, because no single endpoint has everything:
//
//   payment/get_escrow_list        → order_sn + escrow_release_time (settlement)
//   payment/get_escrow_detail_batch→ order_income (every fee)
//   order/get_order_detail         → create_time (order date = tax point)
//
// The third is the existing getOrderDetails() call, so the tax point costs
// nothing extra to obtain. It is order CREATION, not courier hand-off — see
// the note on taxPointDate below. pickup_done_time is requested for the
// order pages but deliberately not read here.

export interface ShopeeFinanceInput {
    // One entry of payment/get_escrow_detail_batch → response[].escrow_detail
    escrowDetail: unknown;
    // Settlement time from payment/get_escrow_list, if the order appeared there.
    escrowReleaseTime?: number | null;
    // One entry of order/get_order_detail → response.order_list[], if fetched.
    orderDetail?: unknown;
}

const num = (source: unknown, path: string): number => {
    const value = _get(source, path);
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const parsed = _toNumber(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

// Fee fields Shopee reports that are NOT independent charges, and must never
// be added to the total.
//
// `credit_card_transaction_fee` restates `seller_transaction_fee` — the same
// charge described twice, once generically and once by payment method. On the
// verification fixture both were 12, and only one of them appears in the
// escrow arithmetic:
//
//   escrow_amount 272 = order_selling_price 340
//                     − commission_fee 42
//                     − seller_transaction_fee 12
//                     − order_ams_commission_fee 13
//                     − seller_order_processing_fee 1
//
// Summing both would overstate fees by 12 baht on every card order and quietly
// break the reconciliation to the bank. reconcileShopee() below is what keeps
// this honest as Shopee adds fields.
//
// `cost_of_goods_sold` is likewise a false friend: on the fixture it is 340,
// equal to order_selling_price. It is the item's value in escrow terms, NOT
// the seller's procurement cost, and reading it as COGS would fabricate a
// 100% margin.

export const normalizeShopeeOrder = (
    input: ShopeeFinanceInput,
    context: {
        storeId: ShopeeStoreId;
        entityId: EntityId;
        fetchedAt: number;
        syncedAt: number;
    },
): FinanceOrderRecord => {
    const detail = input.escrowDetail;
    const income = _get(detail, 'order_income');
    const orderSn = String(_get(detail, 'order_sn') ?? '');

    // Shipping the buyer paid passes through to the carrier; on the fixture
    // buyer_paid_shipping_fee (38) and actual_shipping_fee (38) cancel and do
    // not appear in escrow at all. sellerRevenue is therefore the item side
    // only, which is what ties to escrow_amount.
    const buyerShippingPaid = num(income, 'buyer_paid_shipping_fee');

    // order_DISCOUNTED_price, not order_selling_price. The two agree on
    // every ordinary order, but once a buyer raises a return request
    // Shopee lowers order_selling_price (or drops it) while escrow is still
    // paid on order_discounted_price — 260316ATK9J718: selling 295,
    // discounted 395, escrow 339 = 395 − 56 fees. Reading selling price
    // failed the escrow identity on 20 of the 70 return-request orders in
    // 2026; discounted price ties on all 4,852 Shopee orders (checked
    // 2026-09-13). Selling price stays the fallback for a payload without
    // the discounted field.
    const sellerRevenue =
        _get(income, 'order_discounted_price') != null
            ? num(income, 'order_discounted_price')
            : num(income, 'order_selling_price');

    const fees: FinanceFees = {
        ...emptyFees(),
        commission: num(income, 'commission_fee'),
        // NOT credit_card_transaction_fee — see the note above.
        transaction: num(income, 'seller_transaction_fee'),
        service:
            num(income, 'service_fee') +
            num(income, 'seller_order_processing_fee') +
            num(income, 'campaign_fee') +
            num(income, 'fbs_fee'),
        // Shopee's affiliate marketing solution.
        affiliate: num(income, 'order_ams_commission_fee'),
        // What the courier cost, less what the buyer and Shopee put toward it.
        //
        // `seller_shipping_discount` is deliberately NOT subtracted here, and
        // that is the whole subtlety. Despite the name it is not a discount
        // the seller receives — it is the seller funding free shipping for the
        // buyer, so it is the *reason* buyer_paid_shipping_fee is zero, not a
        // separate credit. Subtracting it cancels the cost it is supposed to
        // explain and understates fees by the full shipping amount on every
        // free-shipping order. Verified against both fixture orders:
        //
        //   paid shipping   38 − 38 − 0 = 0  → 340 − 68  = 272 = escrow ✓
        //   free shipping   38 −  0 − 0 = 38 → 680 − 178 = 502 = escrow ✓
        shippingBorne:
            num(income, 'actual_shipping_fee') -
            buyerShippingPaid -
            num(income, 'shopee_shipping_rebate') -
            num(income, 'shipping_fee_discount_from_3pl'),
        // seller_lost_compensation is NOT here. It is money paid to the
        // seller for a lost parcel, carried in adjustments.compensation;
        // booking it as a negative fee as well understated fees by the
        // claim and broke the escrow identity (26052126AMQ8VQ: ฿88
        // compensation against an ฿88 return refund, escrow 0). Lazada was
        // corrected the same way on 2026-08-06.
        other:
            num(income, 'overseas_return_service_fee') +
            num(income, 'reverse_shipping_fee'),
    };
    fees.total = sumFees(fees);

    return {
        _id: financeRecordId(context.storeId, orderSn),
        storeId: context.storeId,
        platform: 'shopee',
        entityId: context.entityId,
        orderSn,
        // Shopee releases escrow once per order, so the order is the slice.
        part: null,
        currency: 'THB',
        detailLevel: 'order-fees',

        orderDate: _get(input.orderDetail, 'create_time') ?? null,

        // Order creation is the tax point, on every platform, deliberately.
        //
        // §78 fixes the tax point at the EARLIEST of delivery, transfer of
        // ownership, payment, or issue of a tax invoice. Order creation
        // precedes all four, so it can never fall later than the true tax
        // point — and late is the only direction that carries เบี้ยปรับ (up to
        // 2× the tax) plus 1.5%/month. Early merely pays sooner.
        //
        // The usual objection — declaring VAT on an order that is later
        // cancelled — does not arise here: these records are built from
        // escrow, statements and the ledger, which only ever contain
        // transactions that actually settled. There is no unsettled order in
        // this store to reverse.
        //
        // It is also the only date all three platforms supply. Shopee has
        // pickup_done_time and TikTok has rts_time, but Lazada's ledger has no
        // hand-off timestamp at all, so a delivery-based rule would apply a
        // different basis per platform — and an inconsistent basis is far
        // harder to defend in an assessment than a consistent early one.
        taxPointDate: _get(input.orderDetail, 'create_time') ?? null,
        settlementDate: input.escrowReleaseTime ?? null,

        gross: {
            itemsBeforeDiscount: num(income, 'order_original_price'),
            // Seller-funded: reduces the tax base.
            // NOT order_seller_discount as well. On a real seller discount
            // Shopee sets both fields to the same amount (260112SY09B3X1:
            // seller_discount 60, order_seller_discount 60), so adding both
            // doubled it; and on a returned order order_seller_discount
            // holds the refunded amount with seller_discount 0. Checked on
            // all 65 orders carrying it (2026-09-13): 22 genuine discounts,
            // all mirrored in seller_discount; 43 returns.
            sellerDiscount:
                num(income, 'seller_discount') +
                num(income, 'voucher_from_seller'),
            // Platform-funded: the seller is still made whole, so this stays
            // IN the tax base (plan §4.6).
            platformSubsidy:
                num(income, 'shopee_discount') +
                num(income, 'voucher_from_shopee') +
                num(income, 'coins') +
                // A card/payment-channel promotion lowers what the buyer
                // pays but not escrow. `credit_card_promotion` restates it,
                // like credit_card_transaction_fee does the fee — count one.
                num(income, 'payment_promotion'),
            buyerShippingPaid,
            buyerTotal: num(income, 'buyer_total_amount'),
            sellerRevenue,
            // Built from the seller side, so a platform-funded reduction
            // Shopee adds later cannot silently leave the base the way
            // payment_promotion did (2608028VCG4GXQ: buyer paid 332, the
            // supply was 335). Across all 4,852 Shopee orders in 2026 it
            // equals buyer_total_amount + shopee_discount +
            // voucher_from_shopee + coins + payment_promotion on all but 39,
            // and the seller side is the right one on each of those:
            //   9 without returns — buyer_total_amount includes
            //     final_product_protection, an insurance premium Shopee
            //     collects for a third party, not the seller's supply
            //     (2607207G5745T7: buyer 1,008, supply 986);
            //   30 with return requests, where Shopee restates the buyer
            //     side after the return. The base here is before returns,
            //     like the output-VAT tile; a return needs a credit note.
            taxableConsideration: round2(
                num(income, 'order_original_price') -
                    num(income, 'seller_discount') -
                    num(income, 'voucher_from_seller') +
                    buyerShippingPaid,
            ),
        },
        fees,
        adjustments: {
            refund: num(income, 'seller_return_refund'),
            compensation: num(income, 'seller_lost_compensation'),
            other: num(income, 'drc_adjustable_refund'),
        },
        netSettlement: num(income, 'escrow_amount'),

        // Filled by the caller, which knows the entity's VAT status at the
        // resolved tax point. Left null here so this function stays pure and
        // has no opinion about registration.
        vat: null,
        feeVat: null,

        raw: {
            source: 'shopee:get_escrow_detail_batch',
            fetchedAt: context.fetchedAt,
            payload: detail,
        },
        syncedAt: context.syncedAt,
    };
};

// Does the fee taxonomy actually reproduce the money Shopee released?
//
// This is the control that makes the whole report trustworthy. A fee mapped
// into the wrong bucket still sums correctly and hides; a fee counted twice,
// or a new field Shopee adds that we ignore, shows up here as drift. Run it on
// every row at sync time and surface non-zero drift rather than discarding it.
//
// Tolerance is 0.01 THB for satang rounding, not a fudge factor — anything
// larger is a real classification error.
export const reconcileShopee = (
    record: FinanceOrderRecord,
): { ok: boolean; expected: number; reported: number; drift: number } => {
    const expected = record.gross.sellerRevenue - record.fees.total;
    const reported = record.netSettlement;
    const drift = round2(expected - reported);
    return { ok: Math.abs(drift) <= 0.01, expected, reported, drift };
};
